/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import axios from 'axios';
import { API_CONFIG } from './config/api.config';
import { BackingTrack, GenerateTimelineRequest, SCALE_TRANSLATIONS } from './types/jamTrack';

// Add to api.ts
export interface ChordFingering {
  name: string;
  positionString: string;
  fingers: string;
  startingFret: number;
  //difficulty: string;
  //isBarreChord: boolean;
  barreFret?: number;
  positions: ChordPosition[];
}

export interface ChordPosition {
  stringIndex: number;
  fret: number;
  finger?: number;
  isMuted: boolean;
  isOpen: boolean;
  note: string;
}

export interface ChordContext {
  keysWhereCommon: string[];
  commonProgressions: string[];
  musicalStyles: string[];
  emotionalCharacter: string;
}

export interface ChordFingeringResponse {
  chordRoot: string;
  chordType: string;
  displayName: string;
  tuning: string;
  fingerings: ChordFingering[];
  context?: ChordContext;
}


/* export const fetchChordFingerings = async (key: string, chordType: string): Promise<ChordFingeringResponse> => {
  const response = await apiClient.get('/api/Fingerings', {
    params: { key, chordType }
  });
  return response.data;
}; */

export const fetchChordFingerings = async (key: string, chordType: string): Promise<ChordFingering[]> => {
  const response = await apiClient.get('/api/Fingerings', {
    params: { key, chordType }
  });
  return response.data; // This should be an array of ChordFingering objects
};
// Existing interfaces
export interface Note {
  note: string;
  flatAlternativeName: string;
  chromaticPosition: number;
}

export interface Interval {
  intervalName: string;
  abbreviation: string;
  semitonesFromRoot: number;
}

export interface NoteAndInterval {
  note: Note;
  interval: Interval;
}

export interface NoteCollection {
  notesAndIntervals: NoteAndInterval[];
}

export interface CreatorProfileSummary {
  creatorProfileId: number;
  displayName: string;
  slug: string;
  avatarUrl?: string | null;
  heroImageUrl?: string | null;
  creatorType: number;
  websiteUrl?: string | null;
}

export interface CreatorFeaturedLink {
  label: string;
  url: string;
}

export interface CreatorProfileDetail extends CreatorProfileSummary {
  bio?: string | null;
  platformUri?: string | null;
  featuredLinks: CreatorFeaturedLink[];
}

// Chat contracts (align with API)
export interface ChatContextHint {
  scope: string;
  entityId?: string;
  displayName?: string;
  summary?: string;
  metadata?: Record<string, string | null | undefined>;
}

export interface CreateChatSessionRequest {
  sessionId?: string | null;
  persona?: string | null;
  contextHints?: ChatContextHint[] | null;
  clientVersion?: string | null;
}

export interface ChatCitationDto {
  id: string;
  title: string;
  url: string;
  snippet?: string;
  sourceType?: string;
}

export interface ChatSuggestedActionDto {
  id: string;
  title: string;
  prompt: string;
}

export interface ChatClarificationOptionDto {
  id: string;
  label: string;
  description?: string;
}

export interface ChatClarificationDto {
  prompt: string;
  options: ChatClarificationOptionDto[];
}

export type ChatParticipantRole = 'system' | 'user' | 'assistant' | 0 | 1 | 2;

export interface ChatMessageDto {
  id: string;
  role: ChatParticipantRole;
  content: string;
  timestamp: string;
  citations?: ChatCitationDto[];
  suggestions?: ChatSuggestedActionDto[];
  clarification?: ChatClarificationDto;
}

export interface ChatCapabilityLinkDto {
  capabilityCode: string;
  capabilityName: string;
  actionCode: string;
  description: string;
  uiSurface: string;
  clientFunction?: string | null;
  apiEndpoint?: string | null;
  examplePayload?: string | null;
}

export interface ChatSessionResponse {
  sessionId: string;
  createdAt: string;
  persona: string;
  messages: ChatMessageDto[];
  teachingModeAvailable?: boolean;
}

export interface PostChatMessageRequest {
  content: string;
  contextHints?: ChatContextHint[] | null;
  replyToMessageId?: string | null;
  stream?: boolean;
  mode?: string | null;
}

export interface PostChatMessageResponse {
  message: ChatMessageDto;
  additionalMessages?: ChatMessageDto[];
  suggestions?: ChatSuggestedActionDto[];
  clarification?: ChatClarificationDto;
  capabilityLinks?: ChatCapabilityLinkDto[];
}

// Create axios instances
const apiClient = axios.create({
  baseURL: API_CONFIG.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add interceptors if needed
apiClient.interceptors.request.use((config) => {
  // Add auth tokens or other headers for main API
  return config;
});

// Existing API functions using apiClient
export const fetchChordNotes = async (key: string, chordName: string): Promise<NoteCollection> => {
  const response = await apiClient.get('/api/chord/notes', {
    params: { key, chordName },
  });
  return response.data;
};

export const fetchScaleNotes = async (key: string, scaleName: string): Promise<NoteCollection> => {
  const response = await apiClient.get('/api/scale/notes', {
    params: { key, scaleName },
  });
  return response.data;
};

export const getScaleInsights = async (key: string, scaleName: string, config?: any): Promise<any> => {
  try {
    const response = await apiClient.get('/api/scale/insights', {
      params: { key, scaleName },
      ...config,
    });

    if (typeof response.data === 'string') {
      const cleanedResponse = response.data
        .replace(/\\n/g, '')
        .replace(/\/\*.*?\*\//g, '')
        .trim();
      return JSON.parse(cleanedResponse);
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching scale insights:', error);
    throw error;
  }
};

/**
 * Maps pentatonic scales to their parent diatonic scales for chord-of-scale-degree lookups.
 * Pentatonic scales are subsets of diatonic scales and don't have all 7 degrees,
 * so we use the parent scale to get harmonically relevant chords.
 */
const getDiatonicParentScale = (scaleName: string): string => {
  switch (scaleName) {
    case 'pentatonicMajor':
      return 'Ionian';  // C Major Pentatonic derives from C Ionian
    case 'pentatonicMinor':
      return 'aeolian'; // A Minor Pentatonic derives from A Aeolian
    default:
      return scaleName;
  }
};

export const fetchChordOfScaleDegree = async (key: string, scaleName: string, degree: number): Promise<any> => {
  // Map pentatonic scales to their parent diatonic scales
  const diatonicScale = getDiatonicParentScale(scaleName);

  const response = await apiClient.get('/api/chord/chord-of-scale-degree', {
    params: { key, scaleName: diatonicScale, degree },
  });
  return response.data;
};

export const createChatSession = async (
  request?: CreateChatSessionRequest,
  token?: string
): Promise<ChatSessionResponse> => {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await apiClient.post('/chat/sessions', request ?? {}, { headers });
  return response.data;
};

export const sendChatMessage = async (
  sessionId: string,
  request: PostChatMessageRequest,
  token?: string
): Promise<PostChatMessageResponse> => {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await apiClient.post(`/chat/sessions/${sessionId}/messages`, request, { headers });
  return response.data;
};

// ==============================================================================
// TIMELINE / BACKING TRACK API FUNCTIONS
// ==============================================================================

// Get backing tracks compatible with a specific scale
export const fetchBackingTracksByScale = async (key: string, scaleType: string): Promise<BackingTrack[]> => {
  try {
    // Translate frontend scale to backend format
    const backendScale = SCALE_TRANSLATIONS[scaleType] || scaleType;
    
    // URL encode the key to handle sharp symbols properly
    const encodedKey = encodeURIComponent(key);
    const encodedScale = encodeURIComponent(backendScale);
    
    console.log('Fetching backing tracks for:', key, '->', encodedKey, 'scale:', scaleType, '->', backendScale);
    
    const response = await apiClient.get(`/api/timeline/scale/${encodedKey}/${encodedScale}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching backing tracks:', error);
    throw error;
  }
};

// Get a specific backing track by ID
export const fetchBackingTrack = async (id: string): Promise<BackingTrack> => {
  try {
    const response = await apiClient.get(`/api/timeline/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching backing track:', error);
    throw error;
  }
};

// Generate a new timeline for a YouTube video
export const generateTimeline = async (request: GenerateTimelineRequest): Promise<BackingTrack> => {
  try {
    // Translate frontend scale to backend format if provided
    if (request.scale) {
      request.scale = SCALE_TRANSLATIONS[request.scale] || request.scale;
    }
    
    const response = await apiClient.post('/api/timeline/generate', request);
    return response.data;
  } catch (error) {
    console.error('Error generating timeline:', error);
    throw error;
  }
};

// Parse chord notation to extract root and quality for chord notes API
export const parseChordNotation = (chordSymbol: string): { root: string; quality: string } => {
  // Handle basic chord parsing: "Dm" -> { root: "D", quality: "minor" }
  const match = chordSymbol.match(/^([A-G][#b]?)(.*)$/);
  if (!match) return { root: 'C', quality: 'major' };
  
  const root = match[1];
  const suffix = match[2];
  
  // Map common chord suffixes to chord qualities
  const qualityMap: Record<string, string> = {
    'm': 'minor',
    'min': 'minor',
    'maj': 'major',
    'M': 'major',
    '7': 'dominant7',
    'maj7': 'major7',
    'm7': 'minor7',
    'dim': 'diminished',
    '°': 'diminished',
    '+': 'augmented',
    'sus4': 'sus4',
    'sus2': 'sus2',
    '': 'major' // No suffix = major
  };
  
  return {
    root,
    quality: qualityMap[suffix] || 'major'
  };
};

// Get chord notes for timeline chord highlighting
export const fetchChordNotesForTimeline = async (chordSymbol: string): Promise<string[]> => {
  try {
    const { root, quality } = parseChordNotation(chordSymbol);
    const noteCollection = await fetchChordNotes(root, quality);
    return noteCollection?.notes || [];
  } catch (error) {
    console.error('Error fetching chord notes for timeline:', error);
    return [];
  }
};

// Export the client if you need direct access elsewhere
export { apiClient };

export const fetchCreators = async (): Promise<CreatorProfileSummary[]> => {
  const response = await apiClient.get('/api/creators');
  return response.data;
};

export const fetchCreatorById = async (creatorProfileId: number): Promise<CreatorProfileDetail> => {
  const response = await apiClient.get(`/api/creators/${creatorProfileId}`);
  return response.data;
};

// ==============================================================================
// STRIPE / SUBSCRIPTION API FUNCTIONS
// ==============================================================================

export interface StripeConfig {
  publishableKey: string;
}

export interface CheckoutSessionRequest {
  plan?: 'pro_monthly' | 'pro_yearly' | 'premium_monthly' | 'premium_yearly';
  successUrl?: string;
  cancelUrl?: string;
}

export interface CheckoutSessionResponse {
  url: string;
}

export interface PortalSessionResponse {
  url: string;
}

/**
 * Get Stripe publishable key for frontend
 */
export const getStripeConfig = async (): Promise<StripeConfig> => {
  const response = await apiClient.get('/api/stripe/config');
  return response.data;
};

/**
 * Create a Stripe Checkout session for subscription upgrade
 */
export const createCheckoutSession = async (
  request: CheckoutSessionRequest,
  token: string
): Promise<CheckoutSessionResponse> => {
  const response = await apiClient.post('/api/stripe/create-checkout-session', request, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

/**
 * Create a Stripe Customer Portal session for subscription management
 */
export const createPortalSession = async (
  returnUrl: string,
  token: string
): Promise<PortalSessionResponse> => {
  const response = await apiClient.post('/api/stripe/create-portal-session',
    { returnUrl },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};
